import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  CustomTableDeleteCustomValueDocument,
  DeleteCustomTablePageDocument,
  DeleteCustomTablePageResouceValueFragment,
  DeleteCustomTablePageSchemaFieldFragment,
  DeleteCustomTablePageSchemaFragment,
  EmployeesCustomTablesListPageDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import NotFound from 'modules/core/components/NotFound'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getValue } from 'modules/employees/components/CustomResources/SchemaComponent/utils/getValue'
import { RouteContext } from 'modules/employees/pages/CustomTablesListPage/context'

import { LoadingButton, LoadingState } from './LoadingState'

interface CustomFieldValueProps {
  schema: DeleteCustomTablePageSchemaFragment | undefined
  field: DeleteCustomTablePageSchemaFieldFragment
  value?: DeleteCustomTablePageResouceValueFragment
  companyLocale: string
  currency: string
}

function CustomTableValue({
  schema,
  field,
  value,
  companyLocale,
  currency,
}: CustomFieldValueProps) {
  return (
    <Box flexDirection="row" gap="s8">
      <Text.Normal weight="medium">{`${field.labelText}: `}</Text.Normal>
      <Text.Normal>
        {getValue({
          schemaEffectiveAtId: schema?.effectiveAtId,
          field,
          resourceValue: value,
          notAvailable: '',
          companyLocale,
          currency,
        })}
      </Text.Normal>
    </Box>
  )
}

export default function DeleteCustomTablePage() {
  const {
    id: employeeIdparam,
    schemaId,
    valueId,
  } = useStrictParams<'id' | 'schemaId' | 'valueId'>()
  const {
    employee: sessionEmployee,
    company: { locale: companyLocale },
  } = useCurrentSession()

  const employeeId = employeeIdparam ? Number(employeeIdparam) : sessionEmployee.id

  const { data, loading } = useQuery(DeleteCustomTablePageDocument, {
    variables: {
      schemaId: Number(schemaId),
      valueId: Number(valueId),
      employeeId: employeeId,
    },
    skip: !valueId || !schemaId,
  })

  const rootRoute = React.useContext(RouteContext)

  const schema = data?.customResources.schema
  const value = data?.customResources.value
  const currency = data?.employees.employee?.legalEntity?.currency ?? ''

  const navigate = useNavigate()

  const onClose = () => {
    !!rootRoute && navigate(rootRoute.customTablesIndex())
  }

  const [deleteValue] = useMutation(CustomTableDeleteCustomValueDocument, {
    extractorFn: (mutationData) => mutationData.customResourcesMutations.deleteCustomresourcesValue,
    refetchQueries: [EmployeesCustomTablesListPageDocument],
  })

  const { performAction } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    const result = await deleteValue({
      variables: {
        valueId: Number(valueId),
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
    })

    if (handled.success) {
      closePortal()
    }
  })

  return (
    <Modal
      illustration="delete"
      title={i18n.t('custom_tables.delete_modal.title')}
      description={i18n.t('custom_tables.delete_modal.description')}
      onSubmit={performAction}
      onClose={onClose}
      footer={
        loading ? (
          <LoadingButton />
        ) : value ? (
          <Button type="negative" submit>
            {i18n.t('custom_tables.delete_modal.submit')}
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <LoadingState />
      ) : value ? (
        schema?.fields?.map((resource, key) => {
          return (
            resource.field && (
              <CustomTableValue
                key={key}
                schema={schema}
                field={resource.field}
                value={value}
                companyLocale={companyLocale || ''}
                currency={currency}
              />
            )
          )
        })
      ) : (
        <NotFound />
      )}
    </Modal>
  )
}
